import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useLocation,
} from "react-router-dom";
import { PAGE } from "./constants/page";
import AuthService from "./services/AuthService";
import "bootstrap/dist/css/bootstrap.css";
import "./styles/global.scss";
import "./styles/override.scss";
import "./App.scss";
import "./i18n/config";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import NewPatient from "./pages/NewPatient";
import UpdatePatient from "./pages/UpdatePatient";
import Admin from "./pages/admin/Admin";
import AddUsers from "./pages/admin/AddUsers";
import ViewPatientDetails from "./pages/ViewPatientDetails";
import Screening from "./pages/Screening";
import ViewProfile from "./pages/admin/ViewProfile";
import Login from "./components/Login";
import ModalLoader from "./components/ModalLoader";
import { breadCrumbState } from "./redux/reducers/BreadcrumbSlice";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import Breadcrumb2 from "./components/Breadcrumb2";
import { LoaderState } from "./redux/reducers/LoaderSlice";
import StateAdmin from "./pages/admin/StateAdmin";
import GlobalAdmin from "./pages/admin/GlobalAdmin";
import StateDetails from "./components/admin/StateDetails";
import AshasDetails from "./components/admin/AshasDetails";
import PatientsDetails from "./components/admin/PatientsDetails";
import GlobalHome from "./components/GlobalHome";
import { useAuth } from "react-oidc-context";
import UserProfile from "./pages/admin/UserProfile";
import HealthCheck from "./pages/healthCheck/HealthCheck";
import ConfirmationModal from "./components/ConfirmationModal";
import { useIdleTimer } from "react-idle-timer";
import ViewPatient from "./pages/ViewPatient";
import HealthCheckJson from "./pages/healthCheck/HealthCheckJson";
import NotFound from "./pages/NotFound";
import {
  PersistState,
  updateBrand,
  updateLanguage,
  showHeader,
  updateCountry,
  updateKeycloakLocale,
} from "./redux/reducers/PersistSlice";
import i18n from "i18next";
import IdleUserMessage from "./components/IdleUserMessage";
import IntrospectUserMessage from "./components/introspectUserMessage";
import KeyCloakAuthErrors from "./components/KeyCloakAuthErrors";
import { stateLanguageList } from "./constants/options";
import NetworkError from "./pages/NetworkError";

import { isMobile } from "react-device-detect";
import IdleUserMobile from "./components/IdleUserMobile";
import EditAshaProfile from "./pages/admin/EditAshaProfile";
import { ScreeningDetailsState } from "./redux/reducers/ScreeningSlice";
import {
  UserConfigState,
  applyCountryLevelConfig,
} from "./redux/reducers/UserConfigSlice";
import Utility from "./utility/Utility";

function App() {
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const [isApplicationIdle, setApplicationIdle] = useState(false);
  const breadcrumb = useAppSelector(breadCrumbState);
  const loader = useAppSelector(LoaderState);
  const userLanguage = useAppSelector(PersistState);
  const screeningStatus = useAppSelector(ScreeningDetailsState);
  const userConfigDetails: any = useAppSelector(UserConfigState);
  const navigate = useNavigate();
  const idleTimeout = 60000;
  const timeout =
    parseInt(process.env?.REACT_APP_SESSION_EXPIRY_MINUTES || "30") *
    idleTimeout;
  const location = useLocation();
  const languageCookie = Utility.getCookie("KEYCLOAK_LOCALE");

  useEffect(() => {
    if (loader.isError) {
      switch (loader?.error?.statusCode || loader?.error?.code) {
        case "introspectCheck":
          navigate(PAGE.INTROSPECT);
          break;
        case "ERR_NETWORK":
          navigate(PAGE.NETWORK_ERROR);
          break;
        default:
          null;
      }
    }
  }, [loader.error]);

  const onIdle = () => {
    /**set local storage to avoid pcp reloading */
    if (!auth.user?.expired) {
      localStorage.setItem("startScreening", "1");
      setApplicationIdle(true);
    } else {
      handleTimeup();
    }
  };

  const onAction = () => {
    //function required for testing..
    //const d = new Date();
  };

  const handleYes = () => {
    setApplicationIdle(false);
    if (location.pathname.indexOf(PAGE.SCREENING_IFRAME) !== -1) {
      document.dispatchEvent(new CustomEvent("activateIframeTimer"));
    } else {
      activate();
    }
  };

  const handleCancel = async () => {
    setApplicationIdle(false);
    /**remove local storage */
    localStorage.removeItem("startScreening");
    AuthService.instance.logout(userLanguage?.language || "en");
  };

  const handleTimeup = async () => {
    setApplicationIdle(false);
    await AuthService.instance.idleLogout(
      userLanguage?.language || "en",
      auth.user?.expired
    );
  };

  const { activate, getLastActiveTime, pause, resume } = useIdleTimer({
    onIdle,
    onAction,
    timeout,
    crossTab: true,
    events: [
      "keydown",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
    ],
  });

  /**
   * this function will help in mobile to understand idle time is over or not
   * as idleTime npm package not working correct in mobile
   */
  const isIdleTimeout = () => {
    const todaysDate = new Date();
    const lastActiveTime: any = getLastActiveTime()?.getTime();
    const diff = (todaysDate?.getTime() - lastActiveTime) / idleTimeout;
    return diff + 1 > timeout / idleTimeout;
  };

  /*
  To Handle Inactive/Background Tabs Idle Behaviour in mobile devices
  also handling idle session timeout when user token expired
  */
  const handleIdle = async () => {
    if (isMobile && location?.pathname !== "/idle") {
      if (auth.user?.expired || isIdleTimeout()) {
        handleTimeup();
      }
    }
  };

  const searchParams = new URLSearchParams(location?.search);
  if (searchParams.get("l")) {
    const shortCode = searchParams.get("l");
    const language = stateLanguageList.find(languageDetails => {
      if (languageDetails.indexValue === shortCode) {
        return languageDetails;
      }
    });
    dispatch(updateLanguage(language?.value || "en"));
  }

  const handleNetworkError = () => {
    setTimeout(() => {
      navigate(PAGE.NETWORK_ERROR);
    }, 500);
  };

  const handleIframeIdleTime = (e: any) => {
    if (e?.data?.message === "showTimer") {
      setApplicationIdle(true);
    }
    /*if (e?.data?.message === "userActiveInPcp") {
       auth.revokeTokens(["access_token"]);
    }*/
  };

  useEffect(() => {
    if (screeningStatus.isScreening) {
      pause();
    } else {
      resume();
    }
  }, [screeningStatus.isScreening]);

  useEffect(() => {
    /*
      b represent brand here
      we are using this value to toggle between brand logo
    */
    if (searchParams.get("b") === "1") {
      dispatch(updateBrand("1"));
    } else if (searchParams.get("b") === "2") {
      dispatch(updateBrand(""));
    }

    /*
      h represent header here
      we are using this value to toggle to show and hide
    */
    if (searchParams.get("h") === "1") {
      dispatch(showHeader("1"));
    } else if (searchParams.get("h") === "2") {
      dispatch(showHeader(""));
    }

    /*
      c represent country here
      we are using this value to get the country details
    */
    if (searchParams.get("c")) {
      const countryCode = searchParams.get("c") || "in";
      dispatch(applyCountryLevelConfig(countryCode));
      dispatch(updateCountry(countryCode));
    } else {
      if (userLanguage && !userLanguage?.country) {
        dispatch(applyCountryLevelConfig("in"));
        dispatch(updateCountry("in"));
      } else {
        dispatch(applyCountryLevelConfig(userLanguage?.country));
      }
    }

    document.addEventListener("visibilitychange", handleIdle);
    document.addEventListener("NetworkError", handleNetworkError);
    window.addEventListener("message", handleIframeIdleTime, false);
    return () => {
      document.removeEventListener("visibilitychange", handleIdle);
      document.removeEventListener("NetworkError", handleNetworkError);
      window.removeEventListener("message", handleIframeIdleTime, false);
    };
  }, []);

  useEffect(() => {
    if (auth) {
      /*
        1. auth.user - check user logged in - AND
        2. auth.user.expired - check user session has expired - OR
        3. auth.error - check there shouldn't be error
      */
      if (auth.user && (auth.user?.expired || auth?.error !== undefined)) {
        handleTimeup();
      } else if (!auth.isAuthenticated) {
        //do nothing, avoid reload
      } else {
        AuthService.instance.authToken = auth.user;
      }
    }
  }, [auth]);

  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent();
    });
  }, [auth.events, auth.signinSilent]);

  useEffect(() => {
    if (userLanguage?.language == "") {
      i18n.changeLanguage("en");
    } else {
      i18n.changeLanguage(userLanguage?.language);
    }
  }, [userLanguage?.language]);

  useEffect(() => {
    if (
      languageCookie &&
      (userLanguage.keycloakLocale == "" ||
        languageCookie !== userLanguage.keycloakLocale)
    ) {
      dispatch(updateKeycloakLocale(languageCookie));
      dispatch(updateLanguage(languageCookie));
    } else {
      if (searchParams.get("c") || !userLanguage?.language) {
        setTimeout(() => {
          i18n.changeLanguage(userConfigDetails?.defaultLanguage);
        }, 1000);
        dispatch(updateLanguage(userConfigDetails?.defaultLanguage));
      }
    }
  }, [userConfigDetails?.defaultLanguage]);

  return (
    <div>
      <div className="">
        {loader.loading ? <ModalLoader /> : null}

        {auth.isAuthenticated !== true ||
        location.pathname === PAGE.INTROSPECT ? (
          ""
        ) : (
          <Header auth={auth} />
        )}
        {breadcrumb.display && (
          <Breadcrumb2 routes={breadcrumb.routeHistory} navigateTo={navigate} />
        )}
        <Routes>
          <Route
            path={PAGE.GLOBAL_HOME}
            element={
              auth.isAuthenticated === true ? (
                <GlobalHome />
              ) : auth.error !== undefined ? (
                <KeyCloakAuthErrors />
              ) : (
                <Login />
              )
            }
          />
          <Route
            path={PAGE.LOGIN}
            element={!auth.isAuthenticated ? <Login /> : <GlobalHome />}
          />
          <Route
            path={PAGE.HOME}
            index
            element={auth.isAuthenticated === true ? <Home /> : <Login />}
          />
          <Route
            path={PAGE.NEW_PATIENT}
            element={auth.isAuthenticated === true ? <NewPatient /> : <Login />}
          />
          <Route
            path={PAGE.VIEW_PATIENT}
            element={
              auth.isAuthenticated === true ? <ViewPatient /> : <Login />
            }
          />
          <Route
            path={PAGE.EDIT_PATIENT}
            element={
              auth.isAuthenticated === true ? <UpdatePatient /> : <Login />
            }
          />
          <Route
            path={PAGE.VIEW_PATIENT_DETAILS}
            element={
              auth.isAuthenticated === true ? <ViewPatientDetails /> : <Login />
            }
          />
          <Route
            path={PAGE.ADMIN}
            element={auth.isAuthenticated === true ? <Admin /> : <Login />}
          />
          <Route
            path={PAGE.ADD_USERS}
            element={auth.isAuthenticated === true ? <AddUsers /> : <Login />}
          />
          <Route
            path={PAGE.GLOBAL_STATE_ADMIN}
            element={
              auth.isAuthenticated === true ? <GlobalAdmin /> : <Login />
            }
          >
            <Route path="admin" element={<StateDetails />} />
            <Route path="asha" element={<AshasDetails />} />
            <Route path="patient" element={<PatientsDetails />} />
          </Route>
          <Route
            path={PAGE.STATE_ADMIN}
            element={auth.isAuthenticated === true ? <StateAdmin /> : <Login />}
          >
            <Route path="asha" element={<AshasDetails stateAdmin={true} />} />
            <Route path="patient" element={<PatientsDetails />} />
          </Route>
          <Route
            path={PAGE.VIEW_USERS}
            element={
              auth.isAuthenticated === true ? <ViewProfile /> : <Login />
            }
          />
          <Route
            path={PAGE.EDIT_ASHA}
            element={
              auth.isAuthenticated === true ? <ViewProfile /> : <Login />
            }
          />
          <Route
            path={PAGE.UPDATE_ASHA}
            element={
              auth.isAuthenticated === true ? <EditAshaProfile /> : <Login />
            }
          />
          <Route
            path={PAGE.SCREENING_IFRAME}
            element={auth.isAuthenticated === true ? <Screening /> : <Login />}
          />
          <Route
            path={PAGE.USER_PROFILE}
            element={
              auth.isAuthenticated === true ? <UserProfile /> : <Login />
            }
          />
          <Route path={PAGE.HEALTH_CHECK} element={<HealthCheck />} />
          <Route path={PAGE.HEALTH_CHECK_JSON} element={<HealthCheckJson />} />
          <Route
            path={PAGE.IDLESTATE}
            element={!auth.isAuthenticated ? <IdleUserMessage /> : <NotFound />}
          />
          <Route
            path={PAGE.INTROSPECT}
            element={
              auth.isAuthenticated ? <IntrospectUserMessage /> : <Login />
            }
          />
          <Route path="/mobileIdle" element={<IdleUserMobile />} />
          <Route path="/404" element={<NotFound />} />
          <Route path={PAGE.NETWORK_ERROR} element={<NetworkError />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
        <div className="mt-3">
          {auth.isAuthenticated !== true ? "" : <Footer />}
        </div>
      </div>
      {isApplicationIdle && auth.isAuthenticated === true && (
        <ConfirmationModal
          onCancel={handleCancel}
          onYes={handleYes}
          onClose={handleYes}
          showRemainingTime={true}
          onTimeUp={handleTimeup}
          messageKey="session_expired_warning"
          titleKey="session_expired_title"
          yesKey="continue_working"
          cancelkey="logout"
          isCenter={true}
          language={userLanguage?.language || "en"}
        />
      )}
    </div>
  );
}

export default App;
